'use client';

/**
 * Tracks the chart the user is currently viewing — ticker + timeframe AND the
 * currently-visible OHLCV candles (respecting the Bar Replay cursor) — so the
 * global AI assistant popup can analyze EXACTLY what's on screen, live or
 * mid-replay (point-in-time). Set by the stock/ETF chart; read by the popup.
 *
 * Bars go through a REF (not state) so updating them every replay tick / refresh
 * never re-renders consumers (which would loop with the chart that sets them).
 */

import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

export type AssistantBar = { t: string; o: number; h: number; l: number; c: number; v: number };
type ChartMeta = { ticker: string; timeframe: string } | null;

type Ctx = {
  chart: ChartMeta;
  setChart: (c: ChartMeta) => void;
  barsRef: React.MutableRefObject<AssistantBar[] | null>;
};

const AssistantChartCtx = createContext<Ctx>({ chart: null, setChart: () => {}, barsRef: { current: null } });

export function AssistantChartProvider({ children }: { children: React.ReactNode }) {
  const [chart, setChart] = useState<ChartMeta>(null);
  const barsRef = useRef<AssistantBar[] | null>(null);
  return <AssistantChartCtx.Provider value={{ chart, setChart, barsRef }}>{children}</AssistantChartCtx.Provider>;
}

export function useAssistantChart(): ChartMeta {
  return useContext(AssistantChartCtx).chart;
}

/** Popup reads the latest visible bars at send time via this ref. */
export function useAssistantBarsRef(): React.MutableRefObject<AssistantBar[] | null> {
  return useContext(AssistantChartCtx).barsRef;
}

/** Register the currently-viewed chart. Clears on unmount. No-op without a ticker. */
export function useSetAssistantChart(ticker?: string | null, timeframe?: string | null): void {
  const { setChart } = useContext(AssistantChartCtx);
  useEffect(() => {
    if (!ticker) return;
    setChart({ ticker: String(ticker).toUpperCase(), timeframe: timeframe ? String(timeframe) : '' });
    return () => setChart(null);
  }, [ticker, timeframe, setChart]);
}

/** Register the currently-visible candles (writes a ref — no re-render). */
export function useSetAssistantBars(bars: AssistantBar[] | null): void {
  const { barsRef } = useContext(AssistantChartCtx);
  useEffect(() => {
    barsRef.current = bars;
    return () => { barsRef.current = null; };
  }, [bars, barsRef]);
}
